/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Menu configuration
 */
import _ from 'lodash';
import { BILLING_CUSTOMER_PAGE_ROLES, ELIGIBILITY_CUSTOMER_PAGE_ROLES, FAQ_CATEGORIES, getPageFeatures } from './index';
import { ROUTES, PAGE_KEYS, ICON_MAP, getSearchMeta, PAGE_TITLE, ENTITY } from './routes';
import { CUSTOMER_TYPE } from '@veritable/common/constants';

const DEFAULT_SECTION_PROPS: Pick<Menu.Item, 'component' | 'icon' | 'search' | 'faqCategories' | 'roles'> = {
	component: 'PageNotFound',
	icon: ICON_MAP.default,
	search: getSearchMeta()?.default,
	faqCategories: [], // TODO
	roles: [],
};

/**
 * App menu configuration
 * Dynamic required param like /:id in the url path must be only added in extra routes
 * Dynamic optional param like /:tab? in the url path can be added in routes (pages)
 * an item is treated as a section if it has routes, extraRoutes are not supported on a section
 * extraRoutes will not be rendered in the sidebar & its child routes will be ignored
 */
const app = (customerType: App.Customer, features: App.Features): Menu.Item[] => {
	const isEligibilityUser = customerType === CUSTOMER_TYPE.eligibility;
	const isBillingCustomer = customerType === CUSTOMER_TYPE.billing;
	const SEARCH_META = getSearchMeta(isBillingCustomer);
	const PAGE_ROLES = isEligibilityUser ? ELIGIBILITY_CUSTOMER_PAGE_ROLES : BILLING_CUSTOMER_PAGE_ROLES;
	const FEATURES = getPageFeatures(features, isBillingCustomer);
	const items: Menu.Item[] = [
		{
			...DEFAULT_SECTION_PROPS,
			entity: ENTITY.analytics.key,
			key: PAGE_KEYS.analytics.section,
			path: ROUTES.analytics.landing,
			name: 'Analytics',
			routes: [
				{
					entity: ENTITY.analytics.key,
					key: PAGE_KEYS.analytics.dashboard,
					enabled: FEATURES.analytics.dashboard,
					path: ROUTES.analytics.dashboard,
					roles: PAGE_ROLES.analytics.dashboard,
					icon: ICON_MAP.analytics.dashboard,
					search: SEARCH_META.analytics.dashboard,
					name: PAGE_TITLE.analytics.dashboard,
					component: 'AnalyticsDashboard',
					faqCategories: [FAQ_CATEGORIES.Eligibility.category],
				},
			],
		},
		{
			...DEFAULT_SECTION_PROPS,
			entity: ENTITY.claims.key,
			name: ENTITY.claims.title,
			key: PAGE_KEYS.claims.section,
			path: ROUTES.claims.landing,
			routes: [
				{
					entity: ENTITY.claims.key,
					key: PAGE_KEYS.claims.responses,
					enabled: FEATURES.claims.responses,
					path: ROUTES.claims.responses,
					roles: PAGE_ROLES.claims.responses,
					icon: ICON_MAP.claims.responses,
					search: SEARCH_META.claims.responses,
					name: PAGE_TITLE.claims.responses,
					component: DEFAULT_SECTION_PROPS.component,
					faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
					routes: [
						{
							entity: ENTITY.claims.key,
							key: PAGE_KEYS.claims.allResponses,
							enabled: FEATURES.claims.responses,
							path: ROUTES.claims.allResponses,
							roles: PAGE_ROLES.claims.responses,
							icon: ICON_MAP.claims.allResponses,
							search: SEARCH_META.claims.allResponses,
							name: PAGE_TITLE.claims.allResponses,
							component: 'ClaimStatusRequests',
							faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
							hideInMenu: isEligibilityUser,
							extraRoutes: [
								{
									entity: ENTITY.claims.key,
									key: PAGE_KEYS.claims.newRequest,
									enabled: FEATURES.claims.newRequest,
									path: ROUTES.claims.newRequest,
									roles: PAGE_ROLES.claims.newRequest,
									icon: ICON_MAP.claims.newRequest,
									search: SEARCH_META.claims.newRequest,
									name: PAGE_TITLE.claims.newRequest,
									component: 'ClaimStatusRequestForm',
									faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
								},
								{
									entity: ENTITY.claims.key,
									key: PAGE_KEYS.claims.editRequest,
									enabled: FEATURES.claims.editRequest,
									path: ROUTES.claims.editRequest,
									roles: PAGE_ROLES.claims.editRequest,
									icon: ICON_MAP.claims.editRequest,
									search: SEARCH_META.claims.editRequest,
									name: PAGE_TITLE.claims.editRequest,
									component: 'ClaimStatusRequestForm',
									faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
								},
								{
									entity: ENTITY.claims.key,
									key: PAGE_KEYS.claims.viewResponse,
									enabled: FEATURES.claims.viewResponse,
									path: ROUTES.claims.viewResponse,
									roles: PAGE_ROLES.claims.viewResponse,
									icon: ICON_MAP.claims.viewResponse,
									search: SEARCH_META.claims.viewResponse,
									name: PAGE_TITLE.claims.viewResponse,
									component: 'ClaimStatusResponse',
									faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
								},
								{
									entity: ENTITY.claims.key,
									key: PAGE_KEYS.claims.resubmit,
									enabled: FEATURES.claims.resubmit,
									path: ROUTES.claims.resubmit,
									roles: PAGE_ROLES.claims.resubmit,
									icon: ICON_MAP.claims.resubmit,
									search: SEARCH_META.claims.resubmit,
									name: PAGE_TITLE.claims.resubmit,
									component: 'ClaimStatusResubmitForm',
									faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
								},
								{
									entity: ENTITY.claims.key,
									key: PAGE_KEYS.claims.reviewSecondaryClaim,
									enabled: FEATURES.claims.reviewSecondaryClaim,
									path: ROUTES.claims.reviewSecondaryClaim,
									roles: PAGE_ROLES.claims.reviewSecondaryClaim,
									icon: ICON_MAP.claims.reviewSecondaryClaim,
									search: SEARCH_META.claims.reviewSecondaryClaim,
									name: PAGE_TITLE.claims.reviewSecondaryClaim,
									component: 'ClaimStatusResubmitForm',
									faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
								},
								{
									entity: ENTITY.claims.key,
									key: PAGE_KEYS.claims.filePrimaryClaim,
									enabled: FEATURES.claims.filePrimaryClaim,
									path: ROUTES.claims.filePrimaryClaim,
									roles: PAGE_ROLES.claims.filePrimaryClaim,
									icon: ICON_MAP.claims.filePrimaryClaim,
									search: SEARCH_META.claims.filePrimaryClaim,
									name: PAGE_TITLE.claims.filePrimaryClaim,
									component: 'FilePrimaryClaim',
									faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
								},
								{
									entity: ENTITY.claims.key,
									key: PAGE_KEYS.claims.eligibilityResponse,
									enabled: FEATURES.claims.eligibilityResponse,
									path: ROUTES.claims.eligibilityResponse,
									roles: PAGE_ROLES.claims.eligibilityResponse,
									icon: null,
									search: SEARCH_META.claims.eligibility,
									name: PAGE_TITLE.claims.eligibility,
									component: 'EligibilityResponse',
									faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
								},
								{
									entity: ENTITY.claims.key,
									key: PAGE_KEYS.claims.newEligibilityRequest,
									enabled: FEATURES.claims.newEligibilityRequest,
									path: ROUTES.claims.newEligibilityRequest,
									roles: PAGE_ROLES.claims.newEligibilityRequest,
									icon: null,
									search: SEARCH_META.claims.newEligibility,
									name: PAGE_TITLE.claims.newEligibility,
									component: 'EligibilityRequestForm',
									faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
								},
								{
									entity: ENTITY.claims.key,
									key: PAGE_KEYS.claims.editEligibilityRequest,
									enabled: FEATURES.claims.editEligibilityRequest,
									path: ROUTES.claims.editEligibilityRequest,
									roles: PAGE_ROLES.claims.editEligibilityRequest,
									icon: null,
									search: SEARCH_META.claims.eligibilityRequest,
									name: PAGE_TITLE.claims.eligibilityRequest,
									component: 'EligibilityRequestForm',
									faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
								},
							],
						},
						{
							entity: ENTITY.claims.key,
							key: PAGE_KEYS.claims.draftClaims,
							enabled: FEATURES.claims.draftClaims,
							path: ROUTES.claims.draftClaims,
							roles: PAGE_ROLES.claims.responses,
							icon: ICON_MAP.claims.draftClaims,
							search: SEARCH_META.claims.draftClaims,
							name: PAGE_TITLE.claims.draftClaims,
							component: 'DraftClaims',
							faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
						},
						{
							entity: ENTITY.claims.key,
							key: PAGE_KEYS.claims.archivedResponses,
							enabled: FEATURES.claims.archivedClaims,
							path: ROUTES.claims.archivedResponses,
							roles: PAGE_ROLES.claims.responses,
							icon: ICON_MAP.claims.archivedResponses,
							search: SEARCH_META.claims.archivedResponses,
							name: PAGE_TITLE.claims.archivedResponses,
							component: 'ArchivedClaims',
							faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
						},
					],
				},
				{
					entity: ENTITY.claims.key,
					key: PAGE_KEYS.claims.worklist.listView,
					enabled: FEATURES.claims.worklist.listView,
					path: ROUTES.claims.worklist.listView,
					roles: PAGE_ROLES.claims.worklist.listView,
					icon: ICON_MAP.claims.worklist.listView,
					search: SEARCH_META.claims.worklist.listView,
					name: PAGE_TITLE.claims.worklist.listView,
					component: 'AllWorkLists',
					faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
					extraRoutes: [
						{
							entity: ENTITY.claims.key,
							key: PAGE_KEYS.claims.worklist.add,
							enabled: FEATURES.claims.worklist.add,
							path: ROUTES.claims.worklist.add,
							roles: PAGE_ROLES.claims.worklist.add,
							icon: ICON_MAP.claims.worklist.add,
							search: SEARCH_META.claims.worklist.add,
							name: PAGE_TITLE.claims.worklist.add,
							component: 'AutoWorkListForm',
							faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
						},
						{
							entity: ENTITY.claims.key,
							key: PAGE_KEYS.claims.worklist.addManual,
							enabled: FEATURES.claims.worklist.addManual,
							path: ROUTES.claims.worklist.addManual,
							roles: PAGE_ROLES.claims.worklist.addManual,
							icon: ICON_MAP.claims.worklist.addManual,
							search: SEARCH_META.claims.worklist.addManual,
							name: PAGE_TITLE.claims.worklist.addManual,
							component: 'ManualWorkListForm',
							faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
						},
						{
							entity: ENTITY.claims.key,
							key: PAGE_KEYS.claims.worklist.edit,
							enabled: FEATURES.claims.worklist.edit,
							path: ROUTES.claims.worklist.edit,
							roles: PAGE_ROLES.claims.worklist.edit,
							icon: ICON_MAP.claims.worklist.edit,
							search: SEARCH_META.claims.worklist.edit,
							name: PAGE_TITLE.claims.worklist.edit,
							component: 'EditWorkListForm',
							faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
						},
						{
							entity: ENTITY.claims.key,
							key: PAGE_KEYS.claims.worklist.viewResponse,
							enabled: FEATURES.claims.worklist.viewResponse,
							path: ROUTES.claims.worklist.viewResponse,
							roles: PAGE_ROLES.claims.worklist.viewResponse,
							icon: ICON_MAP.claims.worklist.viewResponse,
							search: SEARCH_META.claims.worklist.viewResponse,
							name: PAGE_TITLE.claims.worklist.viewResponse,
							component: 'WorkListResponse',
							faqCategories: [FAQ_CATEGORIES.ClaimStatus.category],
						},
					],
				},
			],
		},
		{
			...DEFAULT_SECTION_PROPS,
			entity: ENTITY.accounts.key,
			name: ENTITY.accounts.title,
			key: PAGE_KEYS.accounts.section,
			path: ROUTES.accounts.landing,
			routes: [
				{
					entity: ENTITY.accounts.key,
					key: PAGE_KEYS.accounts.allAccounts,
					enabled: FEATURES.accounts.allAccounts,
					path: ROUTES.accounts.allAccounts,
					roles: PAGE_ROLES.accounts.allAccounts,
					icon: ICON_MAP.accounts.allAccounts,
					search: SEARCH_META.accounts.allAccounts,
					name: PAGE_TITLE.accounts.allAccounts,
					component: 'Accounts',
					faqCategories: [FAQ_CATEGORIES.Accounts.category],
					extraRoutes: [
						{
							entity: ENTITY.accounts.key,
							key: PAGE_KEYS.accounts.accountDetails,
							enabled: FEATURES.accounts.accountDetails,
							path: ROUTES.accounts.accountDetails,
							roles: PAGE_ROLES.accounts.accountDetails,
							icon: ICON_MAP.accounts.accountDetails,
							search: SEARCH_META.accounts.accountDetails,
							name: PAGE_TITLE.accounts.accountDetails,
							component: 'AccountDetails',
							faqCategories: [FAQ_CATEGORIES.Accounts.category],
						},
						{
							entity: ENTITY.accounts.key,
							key: PAGE_KEYS.accounts.guarantor,
							enabled: FEATURES.accounts.guarantor,
							path: ROUTES.accounts.guarantor,
							roles: PAGE_ROLES.accounts.guarantor,
							icon: ICON_MAP.accounts.guarantor,
							search: SEARCH_META.accounts.guarantor,
							name: PAGE_TITLE.accounts.guarantor,
							component: 'Guarantor',
							faqCategories: [FAQ_CATEGORIES.Accounts.category],
						},
					],
				},
			],
		},
		{
			...DEFAULT_SECTION_PROPS,
			entity: ENTITY.payments.key,
			name: ENTITY.payments.title,
			key: PAGE_KEYS.payments.section,
			path: ROUTES.payments.landing,
			routes: [
				{
					entity: ENTITY.payments.key,
					key: PAGE_KEYS.payments.era.listView,
					enabled: FEATURES.payments.era.listView,
					path: ROUTES.payments.era.listView,
					roles: PAGE_ROLES.payments.era.listView,
					icon: ICON_MAP.payments.era.listView,
					search: SEARCH_META.payments.era.listView,
					name: PAGE_TITLE.payments.era.listView,
					component: 'AllERAs',
					faqCategories: [FAQ_CATEGORIES.Payments.category],
					extraRoutes: [
						{
							entity: ENTITY.payments.key,
							key: PAGE_KEYS.payments.era.viewResponse,
							enabled: FEATURES.payments.era.viewResponse,
							path: ROUTES.payments.era.viewResponse,
							roles: PAGE_ROLES.payments.era.viewResponse,
							icon: ICON_MAP.payments.era.viewResponse,
							search: SEARCH_META.payments.era.viewResponse,
							name: PAGE_TITLE.payments.era.viewResponse,
							component: 'EraResponse',
							faqCategories: [FAQ_CATEGORIES.Payments.category],
						},
					],
				},
				{
					entity: ENTITY.payments.key,
					key: PAGE_KEYS.payments.allPayments,
					enabled: FEATURES.payments.allPayments,
					path: ROUTES.payments.allPayments,
					roles: PAGE_ROLES.payments.allPayments,
					icon: ICON_MAP.payments.allPayments,
					search: SEARCH_META.payments.allPayments,
					name: PAGE_TITLE.payments.allPayments,
					component: 'AllPayments',
					faqCategories: [FAQ_CATEGORIES.Payments.category], // TODO
					extraRoutes: [
						{
							entity: ENTITY.payments.key,
							key: PAGE_KEYS.payments.recordPayment,
							enabled: FEATURES.payments.recordPayment,
							path: ROUTES.payments.recordPayment,
							roles: PAGE_ROLES.payments.recordPayment,
							icon: ICON_MAP.payments.recordPayment,
							search: SEARCH_META.payments.recordPayment,
							name: PAGE_TITLE.payments.recordPayment,
							component: 'PaymentRecordForm',
							faqCategories: [FAQ_CATEGORIES.Payments.category], // TODO
						},
						{
							entity: ENTITY.payments.key,
							key: PAGE_KEYS.payments.paymentAllocation,
							enabled: FEATURES.payments.paymentAllocation,
							path: ROUTES.payments.paymentAllocation,
							roles: PAGE_ROLES.payments.paymentAllocation,
							icon: ICON_MAP.payments.paymentAllocation,
							search: SEARCH_META.payments.paymentAllocation,
							name: PAGE_TITLE.payments.paymentAllocation,
							component: 'PaymentAllocation',
							faqCategories: [FAQ_CATEGORIES.Payments.category], // TODO
						},
					],
				},
			],
		},
		{
			...DEFAULT_SECTION_PROPS,
			entity: ENTITY.patients.key,
			name: ENTITY.patients.title,
			key: PAGE_KEYS.patients.section,
			path: ROUTES.patients.landing,
			routes: [
				{
					entity: ENTITY.patients.key,
					key: PAGE_KEYS.patients.allPatients,
					enabled: FEATURES.patients.allPatients,
					path: ROUTES.patients.allPatients,
					roles: PAGE_ROLES.patients.allPatients,
					icon: ICON_MAP.patients.allPatients,
					search: SEARCH_META.patients.allPatients,
					name: PAGE_TITLE.patients.allPatients,
					component: 'Patients',
					faqCategories: [FAQ_CATEGORIES.Patients.category],
					extraRoutes: [
						{
							entity: ENTITY.patients.key,
							key: PAGE_KEYS.patients.PatientDetails,
							enabled: FEATURES.patients.PatientDetails,
							path: ROUTES.patients.PatientDetails,
							roles: PAGE_ROLES.patients.PatientDetails,
							icon: ICON_MAP.patients.PatientDetails,
							search: SEARCH_META.patients.PatientDetails,
							name: PAGE_TITLE.patients.PatientDetails,
							component: 'PatientDetails',
							faqCategories: [FAQ_CATEGORIES.Patients.category],
						},
					],
				},
			],
		},
		{
			...DEFAULT_SECTION_PROPS,
			entity: ENTITY.eligibility.key,
			name: ENTITY.eligibility.title,
			key: PAGE_KEYS.eligibility.section,
			path: ROUTES.eligibility.landing,
			routes: [
				{
					entity: ENTITY.eligibility.key,
					key: PAGE_KEYS.eligibility.responses,
					enabled: FEATURES.eligibility.responses,
					path: ROUTES.eligibility.responses,
					roles: PAGE_ROLES.eligibility.responses,
					icon: ICON_MAP.eligibility.responses,
					search: SEARCH_META.eligibility.responses,
					name: PAGE_TITLE.eligibility.responses,
					component: 'EligibilityRequests',
					faqCategories: [FAQ_CATEGORIES.Eligibility.category],
					extraRoutes: [
						{
							entity: ENTITY.eligibility.key,
							key: PAGE_KEYS.eligibility.newRequest,
							enabled: FEATURES.eligibility.newRequest,
							path: ROUTES.eligibility.newRequest,
							roles: PAGE_ROLES.eligibility.newRequest,
							icon: ICON_MAP.eligibility.newRequest,
							search: SEARCH_META.eligibility.newRequest,
							name: PAGE_TITLE.eligibility.newRequest,
							component: 'EligibilityRequestForm',
							faqCategories: [FAQ_CATEGORIES.Eligibility.category],
						},
						{
							entity: ENTITY.eligibility.key,
							key: PAGE_KEYS.eligibility.viewResponse,
							enabled: FEATURES.eligibility.viewResponse,
							path: ROUTES.eligibility.viewResponse,
							roles: PAGE_ROLES.eligibility.viewResponse,
							icon: ICON_MAP.eligibility.viewResponse,
							search: SEARCH_META.eligibility.viewResponse,
							name: PAGE_TITLE.eligibility.viewResponse,
							component: 'EligibilityResponse',
							faqCategories: [FAQ_CATEGORIES.Eligibility.category],
						},
						{
							entity: ENTITY.eligibility.key,
							key: PAGE_KEYS.eligibility.editRequest,
							enabled: FEATURES.eligibility.editRequest,
							path: ROUTES.eligibility.editRequest,
							roles: PAGE_ROLES.eligibility.editRequest,
							icon: ICON_MAP.eligibility.editRequest,
							search: SEARCH_META.eligibility.editRequest,
							name: PAGE_TITLE.eligibility.editRequest,
							component: 'EligibilityRequestForm',
							faqCategories: [FAQ_CATEGORIES.Eligibility.category],
						},
						{
							entity: ENTITY.eligibility.key,
							key: PAGE_KEYS.eligibility.batch.viewResponse,
							enabled: FEATURES.eligibility.batch.viewResponse,
							path: ROUTES.eligibility.batch.viewResponse,
							roles: PAGE_ROLES.eligibility.batch.viewResponse,
							icon: ICON_MAP.eligibility.batch.viewResponse,
							search: SEARCH_META.eligibility.batch.viewResponse,
							name: PAGE_TITLE.eligibility.batch.viewResponse,
							component: 'EligibilityBatchResponse',
							faqCategories: [FAQ_CATEGORIES.Eligibility.category],
						},
						{
							entity: ENTITY.eligibility.key,
							key: PAGE_KEYS.eligibility.batch.add,
							enabled: FEATURES.eligibility.batch.add,
							path: ROUTES.eligibility.batch.add,
							roles: PAGE_ROLES.eligibility.batch.add,
							icon: ICON_MAP.eligibility.batch.add,
							search: SEARCH_META.eligibility.batch.add,
							name: PAGE_TITLE.eligibility.batch.add,
							component: 'EligibilityBatchUpload',
							faqCategories: [FAQ_CATEGORIES.Eligibility.category],
						},
						{
							entity: ENTITY.eligibility.key,
							key: PAGE_KEYS.eligibility.batch.edit,
							enabled: FEATURES.eligibility.batch.edit,
							path: ROUTES.eligibility.batch.edit,
							roles: PAGE_ROLES.eligibility.batch.edit,
							icon: ICON_MAP.eligibility.batch.edit,
							search: SEARCH_META.eligibility.batch.edit,
							name: PAGE_TITLE.eligibility.batch.edit,
							component: 'EligibilityBatchUpload',
							faqCategories: [FAQ_CATEGORIES.Eligibility.category],
						},
					],
				},
			],
		},
		{
			...DEFAULT_SECTION_PROPS,
			entity: ENTITY.codeExplorer.key,
			name: ENTITY.codeExplorer.title,
			key: PAGE_KEYS.codeExplorer.section,
			path: ROUTES.codeExplorer.landing,
			routes: [
				{
					entity: ENTITY.codeExplorer.key,
					key: PAGE_KEYS.codeExplorer.allCodes,
					enabled: FEATURES.codeExplorer.allCodes,
					path: ROUTES.codeExplorer.allCodes,
					name: PAGE_TITLE.codeExplorer.allCodes,
					icon: ICON_MAP.codeExplorer.allCodes,
					component: 'CodeExplorer',
					search: SEARCH_META.codeExplorer.allCodes,
					roles: PAGE_ROLES.codeExplorer.allCodes,
					faqCategories: [FAQ_CATEGORIES.CodeExplorer.category], // TODO
					extraRoutes: [
						{
							entity: ENTITY.codeExplorer.key,
							key: PAGE_KEYS.codeExplorer.searchResults,
							enabled: FEATURES.codeExplorer.searchResults,
							path: ROUTES.codeExplorer.searchResults,
							roles: PAGE_ROLES.codeExplorer.searchResults,
							name: PAGE_TITLE.codeExplorer.searchResults,
							icon: ICON_MAP.codeExplorer.searchResults,
							component: 'SearchResults',
							search: SEARCH_META.codeExplorer.searchResults,
							faqCategories: [FAQ_CATEGORIES.CodeExplorer.category], // TODO
						},
						{
							entity: ENTITY.codeExplorer.key,
							key: PAGE_KEYS.codeExplorer.codeSetSearchResults,
							enabled: FEATURES.codeExplorer.searchResults,
							path: ROUTES.codeExplorer.codeSetSearchResults,
							roles: PAGE_ROLES.codeExplorer.searchResults,
							name: PAGE_TITLE.codeExplorer.searchResults,
							icon: ICON_MAP.codeExplorer.searchResults,
							component: 'SearchResults',
							search: SEARCH_META.codeExplorer.searchResults,
							faqCategories: [FAQ_CATEGORIES.CodeExplorer.category], // TODO
						},
						{
							entity: ENTITY.codeExplorer.key,
							key: PAGE_KEYS.codeExplorer.codeDetails,
							enabled: FEATURES.codeExplorer.codeDetails,
							path: ROUTES.codeExplorer.codeDetails,
							roles: PAGE_ROLES.codeExplorer.codeDetails,
							name: PAGE_TITLE.codeExplorer.codeDetails,
							icon: ICON_MAP.codeExplorer.codeDetails,
							component: 'DetailedCodeView',
							search: SEARCH_META.codeExplorer.codeDetails,
							faqCategories: [FAQ_CATEGORIES.CodeExplorer.category], // TODO
						},
						{
							entity: ENTITY.codeExplorer.key,
							key: PAGE_KEYS.codeExplorer.codeList,
							enabled: FEATURES.codeExplorer.codeList,
							path: ROUTES.codeExplorer.codeList,
							roles: PAGE_ROLES.codeExplorer.codeList,
							name: PAGE_TITLE.codeExplorer.codeList,
							icon: ICON_MAP.codeExplorer.codeList,
							component: 'ICD10PCSLookup',
							search: SEARCH_META.codeExplorer.codeList,
							faqCategories: [FAQ_CATEGORIES.CodeExplorer.category], // TODO
						},
					],
				},
				{
					entity: ENTITY.codeExplorer.key,
					key: PAGE_KEYS.codeExplorer.favoriteCodes,
					enabled: FEATURES.codeExplorer.favoriteCodes,
					path: ROUTES.codeExplorer.favoriteCodes,
					name: PAGE_TITLE.codeExplorer.favoriteCodes,
					icon: ICON_MAP.codeExplorer.favoriteCodes,
					component: 'FavoriteCodes',
					search: SEARCH_META.codeExplorer.favoriteCodes,
					roles: PAGE_ROLES.codeExplorer.favoriteCodes,
					faqCategories: [FAQ_CATEGORIES.CodeExplorer.category], // TODO
				},
			],
		},
	];

	if (isBillingCustomer) {
		return items;
	}
	const ELIGIBILITY_CUSTOMER_SECTION_ORDER: Record<string, number> = {
		[PAGE_KEYS.eligibility.section]: 0,
	};
	return _.sortBy(items, (item) => ELIGIBILITY_CUSTOMER_SECTION_ORDER[item.key] ?? Infinity);
};

/**
 * Settings menu configuration
 * Dynamic required param like /:id in the url path must be only added in extra routes
 * Dynamic optional param like /:tab? in the url path can be added in routes (pages)
 * extraRoutes will not be rendered in the sidebar & its child routes will be ignored
 */
const settings = (customerType: App.Customer, features: App.Features): Menu.Item[] => {
	const isBillingCustomer = customerType === CUSTOMER_TYPE.billing;
	const SEARCH_META = getSearchMeta(isBillingCustomer);
	const PAGE_ROLES = customerType === CUSTOMER_TYPE.eligibility ? ELIGIBILITY_CUSTOMER_PAGE_ROLES : BILLING_CUSTOMER_PAGE_ROLES;
	const FEATURES = getPageFeatures(features, isBillingCustomer);
	return [
		{
			...DEFAULT_SECTION_PROPS,
			entity: ENTITY.settings.key,
			key: PAGE_KEYS.settings.organization.section,
			path: ROUTES.settings.organization.landing,
			name: 'Organization',
			routes: [
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.organization.general,
					enabled: FEATURES.settings.organization.general,
					path: ROUTES.settings.organization.general,
					roles: PAGE_ROLES.settings.organization.general,
					icon: ICON_MAP.settings.organization.general,
					search: SEARCH_META.settings.organization.general,
					name: PAGE_TITLE.settings.organization.general,
					component: 'GeneralSettings',
					faqCategories: [], // TODO
				},
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.organization.users.listView,
					enabled: FEATURES.settings.organization.users.listView,
					path: ROUTES.settings.organization.users.listView,
					roles: PAGE_ROLES.settings.organization.users.listView,
					icon: ICON_MAP.settings.organization.users.listView,
					search: SEARCH_META.settings.organization.users.listView,
					name: PAGE_TITLE.settings.organization.users.listView,
					component: 'UserManagement',
					faqCategories: [], // TODO
					extraRoutes: [
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.organization.users.add,
							enabled: FEATURES.settings.organization.users.add,
							path: ROUTES.settings.organization.users.add,
							roles: PAGE_ROLES.settings.organization.users.add,
							icon: ICON_MAP.settings.organization.users.add,
							search: SEARCH_META.settings.organization.users.add,
							name: PAGE_TITLE.settings.organization.users.add,
							component: 'UserManagementForm',
							faqCategories: [], // TODO
						},
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.organization.users.edit,
							enabled: FEATURES.settings.organization.users.edit,
							path: ROUTES.settings.organization.users.edit,
							roles: PAGE_ROLES.settings.organization.users.edit,
							icon: ICON_MAP.settings.organization.users.edit,
							search: SEARCH_META.settings.organization.users.edit,
							name: PAGE_TITLE.settings.organization.users.edit,
							component: 'UserManagementForm',
							faqCategories: [], // TODO
						},
					],
				},
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.organization.subscriptions,
					enabled: FEATURES.settings.organization.subscriptions,
					path: ROUTES.settings.organization.subscriptions,
					roles: PAGE_ROLES.settings.organization.subscriptions,
					icon: ICON_MAP.settings.organization.subscriptions,
					search: SEARCH_META.settings.organization.subscriptions,
					name: PAGE_TITLE.settings.organization.subscriptions,
					component: 'Subscriptions',
					faqCategories: [], // TODO
				},
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.organization.auditLogs,
					enabled: FEATURES.settings.organization.auditLogs,
					path: ROUTES.settings.organization.auditLogs,
					roles: PAGE_ROLES.settings.organization.auditLogs,
					icon: ICON_MAP.settings.organization.auditLogs,
					search: SEARCH_META.settings.organization.auditLogs,
					name: PAGE_TITLE.settings.organization.auditLogs,
					component: 'AuditLogs',
					faqCategories: [], // TODO
				},
			],
		},
		{
			...DEFAULT_SECTION_PROPS,
			entity: ENTITY.settings.key,
			key: PAGE_KEYS.settings.dataManagement.section,
			path: ROUTES.settings.dataManagement.landing,
			name: 'Data Management',
			routes: [
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.dataManagement.providers.listView,
					enabled: FEATURES.settings.dataManagement.providers.listView,
					path: ROUTES.settings.dataManagement.providers.listView,
					roles: PAGE_ROLES.settings.dataManagement.providers.listView,
					icon: ICON_MAP.settings.dataManagement.providers.listView,
					search: SEARCH_META.settings.dataManagement.providers.listView,
					name: PAGE_TITLE.settings.dataManagement.providers.listView,
					component: 'Providers',
					faqCategories: [FAQ_CATEGORIES.Provider.category],
					extraRoutes: [
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.dataManagement.providers.add,
							enabled: FEATURES.settings.dataManagement.providers.add,
							path: ROUTES.settings.dataManagement.providers.add,
							roles: PAGE_ROLES.settings.dataManagement.providers.add,
							icon: ICON_MAP.settings.dataManagement.providers.add,
							search: SEARCH_META.settings.dataManagement.providers.add,
							name: PAGE_TITLE.settings.dataManagement.providers.add,
							component: 'ProviderForm',
							faqCategories: [FAQ_CATEGORIES.Provider.category],
						},
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.dataManagement.providers.edit,
							enabled: FEATURES.settings.dataManagement.providers.edit,
							path: ROUTES.settings.dataManagement.providers.edit,
							roles: PAGE_ROLES.settings.dataManagement.providers.edit,
							icon: ICON_MAP.settings.dataManagement.providers.edit,
							search: SEARCH_META.settings.dataManagement.providers.edit,
							name: PAGE_TITLE.settings.dataManagement.providers.edit,
							component: 'ProviderForm',
							faqCategories: [FAQ_CATEGORIES.Provider.category],
						},
					],
				},
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.dataManagement.locations.listView,
					enabled: FEATURES.settings.dataManagement.locations.listView,
					path: ROUTES.settings.dataManagement.locations.listView,
					roles: PAGE_ROLES.settings.dataManagement.locations.listView,
					icon: ICON_MAP.settings.dataManagement.locations.listView,
					search: SEARCH_META.settings.dataManagement.locations.listView,
					name: PAGE_TITLE.settings.dataManagement.locations.listView,
					component: 'Locations',
					faqCategories: [], // TODO
					extraRoutes: [
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.dataManagement.locations.add,
							enabled: FEATURES.settings.dataManagement.locations.add,
							path: ROUTES.settings.dataManagement.locations.add,
							roles: PAGE_ROLES.settings.dataManagement.locations.add,
							icon: ICON_MAP.settings.dataManagement.locations.add,
							search: SEARCH_META.settings.dataManagement.locations.add,
							name: PAGE_TITLE.settings.dataManagement.locations.add,
							component: 'LocationForm',
							faqCategories: [], // TODO
						},
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.dataManagement.locations.edit,
							enabled: FEATURES.settings.dataManagement.locations.edit,
							path: ROUTES.settings.dataManagement.locations.edit,
							roles: PAGE_ROLES.settings.dataManagement.locations.edit,
							icon: ICON_MAP.settings.dataManagement.locations.edit,
							search: SEARCH_META.settings.dataManagement.locations.edit,
							name: PAGE_TITLE.settings.dataManagement.locations.edit,
							component: 'LocationForm',
							faqCategories: [], // TODO
						},
					],
				},
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.dataManagement.payers.listView,
					enabled: FEATURES.settings.dataManagement.payers.listView,
					path: ROUTES.settings.dataManagement.payers.listView,
					roles: PAGE_ROLES.settings.dataManagement.payers.listView,
					icon: ICON_MAP.settings.dataManagement.payers.listView,
					search: SEARCH_META.settings.dataManagement.payers.listView,
					name: PAGE_TITLE.settings.dataManagement.payers.listView,
					component: 'Payers',
					faqCategories: [FAQ_CATEGORIES.Payer.category],
					extraRoutes: [
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.dataManagement.payers.edit,
							enabled: FEATURES.settings.dataManagement.payers.edit,
							path: ROUTES.settings.dataManagement.payers.edit,
							roles: PAGE_ROLES.settings.dataManagement.payers.edit,
							icon: ICON_MAP.settings.dataManagement.payers.edit,
							search: SEARCH_META.settings.dataManagement.payers.edit,
							name: PAGE_TITLE.settings.dataManagement.payers.edit,
							component: 'EligibilityPayerSettings',
							faqCategories: [FAQ_CATEGORIES.Payer.category],
						},
					],
				},
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.dataManagement.worklists.config,
					enabled: FEATURES.settings.dataManagement.worklists.config,
					path: ROUTES.settings.dataManagement.worklists.config,
					roles: PAGE_ROLES.settings.dataManagement.worklists.config,
					icon: ICON_MAP.settings.dataManagement.worklists.config,
					search: SEARCH_META.settings.dataManagement.worklists.config,
					name: PAGE_TITLE.settings.dataManagement.worklists.config,
					component: 'WorklistSettings',
					// TODO
					faqCategories: [],
				},
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.dataManagement.claims.config,
					enabled: FEATURES.settings.dataManagement.claims.config,
					path: ROUTES.settings.dataManagement.claims.config,
					roles: PAGE_ROLES.settings.dataManagement.claims.config,
					icon: ICON_MAP.settings.dataManagement.claims.config,
					search: SEARCH_META.settings.dataManagement.claims.config,
					name: PAGE_TITLE.settings.dataManagement.claims.config,
					component: 'ClaimSettings',
					// TODO
					faqCategories: [],
				},
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.dataManagement.customAttributes.listView,
					enabled: FEATURES.settings.dataManagement.customAttributes.listView,
					path: ROUTES.settings.dataManagement.customAttributes.listView,
					roles: PAGE_ROLES.settings.dataManagement.customAttributes.listView,
					icon: ICON_MAP.settings.dataManagement.customAttributes.listView,
					search: SEARCH_META.settings.dataManagement.customAttributes.listView,
					name: PAGE_TITLE.settings.dataManagement.customAttributes.listView,
					component: 'CustomAttributes',
					faqCategories: [], // TODO
					extraRoutes: [
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.dataManagement.customAttributes.add,
							enabled: FEATURES.settings.dataManagement.customAttributes.add,
							path: ROUTES.settings.dataManagement.customAttributes.add,
							roles: PAGE_ROLES.settings.dataManagement.customAttributes.add,
							icon: ICON_MAP.settings.dataManagement.customAttributes.add,
							search: SEARCH_META.settings.dataManagement.customAttributes.add,
							name: PAGE_TITLE.settings.dataManagement.customAttributes.add,
							component: 'CustomAttributeForm',
							faqCategories: [], // TODO
						},
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.dataManagement.customAttributes.edit,
							enabled: FEATURES.settings.dataManagement.customAttributes.edit,
							path: ROUTES.settings.dataManagement.customAttributes.edit,
							roles: PAGE_ROLES.settings.dataManagement.customAttributes.edit,
							icon: ICON_MAP.settings.dataManagement.customAttributes.edit,
							search: SEARCH_META.settings.dataManagement.customAttributes.edit,
							name: PAGE_TITLE.settings.dataManagement.customAttributes.edit,
							component: 'CustomAttributeForm',
							faqCategories: [], // TODO
						},
					],
				},
			],
		},
		{
			...DEFAULT_SECTION_PROPS,
			entity: ENTITY.settings.key,
			key: PAGE_KEYS.settings.integration.section,
			path: ROUTES.settings.integration.landing,
			name: 'Integration',
			routes: [
				{
					entity: ENTITY.settings.key,
					key: PAGE_KEYS.settings.integration.clearingHouse.listView,
					enabled: FEATURES.settings.integration.clearingHouse.listView,
					path: ROUTES.settings.integration.clearingHouse.listView,
					roles: PAGE_ROLES.settings.integration.clearingHouse.listView,
					search: SEARCH_META.settings.integration.clearingHouse.listView,
					name: PAGE_TITLE.settings.integration.clearingHouse.listView,
					icon: ICON_MAP.settings.integration.clearingHouse.listView,
					component: 'AllClearinghouse',
					faqCategories: [FAQ_CATEGORIES.ClearingHouse.category],
					extraRoutes: [
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.integration.clearingHouse.add,
							enabled: FEATURES.settings.integration.clearingHouse.add,
							path: ROUTES.settings.integration.clearingHouse.add,
							roles: PAGE_ROLES.settings.integration.clearingHouse.add,
							icon: ICON_MAP.settings.integration.clearingHouse.add,
							search: SEARCH_META.settings.integration.clearingHouse.add,
							name: PAGE_TITLE.settings.integration.clearingHouse.add,
							component: 'ClearingHouseForm',
							faqCategories: [FAQ_CATEGORIES.ClearingHouse.category],
						},
						{
							entity: ENTITY.settings.key,
							key: PAGE_KEYS.settings.integration.clearingHouse.edit,
							enabled: FEATURES.settings.integration.clearingHouse.edit,
							path: ROUTES.settings.integration.clearingHouse.edit,
							roles: PAGE_ROLES.settings.integration.clearingHouse.edit,
							icon: ICON_MAP.settings.integration.clearingHouse.edit,
							search: SEARCH_META.settings.integration.clearingHouse.edit,
							name: PAGE_TITLE.settings.integration.clearingHouse.edit,
							component: 'ClearingHouseForm',
							faqCategories: [FAQ_CATEGORIES.ClearingHouse.category],
						},
					],
				},
			],
		},
	];
};

const help: Menu.Item = {
	entity: ENTITY.help.key,
	key: PAGE_KEYS.help.landing,
	name: PAGE_TITLE.help.landing,
	path: ROUTES.help.landing,
	roles: [],
	icon: ICON_MAP.help.landing,
	search: getSearchMeta().help.landing,
	component: 'UserHelp',
	isExtraRoute: true,
	faqCategories: [], // TODO
};

export { DEFAULT_SECTION_PROPS };
export default { app, settings, help, DEFAULT_SECTION_PROPS };
